<template>
  <div>
    <div v-if="!loading" class="subscriptions-wrap">
      <div v-if="!isUserPremium || isFreePremium">
        <div v-if="!isFreePremium" key="1">{{ $gettext('You currently have a Basic account, upgrade to Premium to unlock all gekko features.') }} <a href='https://www.getgekko.com/en/pricing/' target='_blank' style="font-size: 1rem;">{{ $gettext('Compare Basic to Premium') }}</a></div>

        <section>
          <PlanBoxPremium 
            :price-data="planPriceData" 
            :total-discount="totalDiscount" 
            :trigger-amount="activeDiscount.triggerAmount" 
            :isFreePremium="isFreePremium" 
            @create-plan="createPlan"
            @upgrade-free-plan="upgradeFreePlan">

            <template v-slot:planprice>
              <div class="plan-box-upgrade-teamsize">
                <div class="upgrade-teamsize-content-wrap non-premium">
                  <div class="strong">{{ $gettext('Gekko Premium') }}</div>
                  <div class="strong">{{ premiumCost }} {{ $gettext('EUR') }}</div>
                </div>
              </div>
            </template>

            <template v-slot:teamsize>
              <div class="plan-box-upgrade-teamsize" style="margin-bottom: 2rem;">
                <div class="upgrade-teamsize-content-wrap non-premium">
                  <div class="strong">{{ $gettext('Additional Users') }}</div>
                  <TeamsizeSelector />
                  <div class="strong">{{ (teamSize * teamCost) }} {{ $gettext('EUR') }}</div>
                </div>
              </div>
            </template>

            <template v-slot:billingcycle>
              <div>
                <span class="billingcycle-date">{{ $gettext('End date') }}&nbsp;{{ endBillingDate }}</span>
                <div class="plan-cycle-switch-wrap">
                  <span @click="yearlyCycle = false" class="left-selector">{{ $gettext('Monthly') }}</span>
                  <b-form-checkbox v-model="yearlyCycle" switch size="lg">
                    <span class="right-selector">{{ $gettext("Yearly") }} <span class="yearly-offer">{{ discountText }}</span></span>
                  </b-form-checkbox>
                </div>
              </div>
            </template>

          </PlanBoxPremium>
        </section>
      </div>

      <div v-if="isUserPremium && !isFreePremium">
        <section class="premium-subscription-wrapper">
          <div class="plan-box">
            <div class="plan-box-item">
              <div class="plan-box-item__title">{{ $gettext('Subscription type') }}</div>
              <div style="width: 300px; display: flex; justify-content: space-between;">
                <span>{{ $gettext('Premium') }}</span> 
                <span>€{{ getProductByType('premium').cost * selectedCycleMultiplier }}</span>
              </div>
            </div>
            <div class="plan-box-item">
              <div class="plan-box-item__title">
                {{ $gettext('Additional Active users') }}
                <font-awesome-icon id="tooltip-additionalusers" class="tooltip-icon tooltip-icon--question ml-2" icon="fa-regular fa-circle-question" />
                <b-tooltip target="tooltip-additionalusers" triggers="hover">
                  <div class="tooltip-text">
                      {{ $gettext('Gekko only counts active team slots and automatically stops billing team slots for old teammembers that are no longer active.') }}
                  </div>
                </b-tooltip>
              </div>
              <div style="width: 300px; display: flex; justify-content: space-between;">
                <span>{{ usedTeamSlots }}x</span> 
                <span>€{{ usedTeamSlots * getProductByType('team').cost * selectedCycleMultiplier }}</span>
              </div>
            </div>
            <div class="plan-box-item">
              <div class="plan-box-item__title">{{ $gettext('Payment method') }}</div>
              <div v-if="paymentDetails.data">
                <!-- FRONTEND_AUDIT -->
                <div v-if="paymentDetails.data.tokens[0].code === 'IDEAL'">
                  {{ $gettext('Bank account') }} IBAN {{ paymentDetails.data.tokens[0].display }}
                  <router-link v-if="!isGrace" :to="{ name: 'PaymentDetails' }" href="#update"><font-awesome-icon style="color: #e4c87e;" icon="fa-solid fa-pencil" class="ml-4" /></router-link>
                </div>
                <div v-else>
                  {{ paymentDetails.data.tokens[0].display }}
                  <router-link v-if="!isGrace" :to="{ name: 'PaymentDetails' }" href="#update"><font-awesome-icon style="color: #e4c87e;" icon="fa-solid fa-pencil" class="ml-4" /></router-link>
                </div>
              </div>
              <div v-else>
                <div v-if="paymentDetails && paymentDetails.details[0].RecurringDetail['bank']">
                  {{ $gettext('Bank account') }} IBAN {{ paymentDetails.details[0].RecurringDetail['bank']['iban'] }} <router-link v-if="!isGrace" :to="{ name: 'PaymentDetails' }" href="#update"><font-awesome-icon style="color: #e4c87e;" icon="fa-solid fa-pencil" class="ml-4" /></router-link>
                </div>
                <div v-if="paymentDetails && paymentDetails.details[0].RecurringDetail['card']">
                  {{ $gettext('Credit card') }} **** **** **** {{ paymentDetails.details[0].RecurringDetail['card']['number'] }} <router-link v-if="!isGrace" :to="{ name: 'PaymentDetails' }" href="#update"><font-awesome-icon style="color: #e4c87e;" icon="fa-solid fa-pencil" class="ml-4" /></router-link>
                </div>
                <div v-if="!paymentDetails">
                  {{ $gettext('You have not set any payment details.') }} <router-link v-if="!isGrace" :to="{ name: 'PaymentDetails' }" href="#update">{{ $gettext('Add payment method') }}</router-link>.
                </div>
              </div>
            </div>
            <div class="plan-box-item">
              <div class="plan-box-item__title">{{ $gettext('Valid until') }}</div>
              {{ activePlan.end_date | formatDate }}
            </div>
            <div class="plan-box-item">
              <div class="plan-box-item__title">{{ $gettext('Billing cycle') }}</div>
              <span v-if="!showBillingSwitch && !isGrace">
                {{ (yearlyCycle) ? $gettext('Yearly') : $gettext('Monthly') }} <font-awesome-icon @click="showBillingSwitch=true" style="color: #e4c87e; cursor: pointer;" icon="fa-solid fa-pencil" class="ml-4" />
              </span>
              <div v-if="showBillingSwitch">
                <div class="plan-cycle-switch-wrap" style="justify-content: flex-start;">
                  <span @click="yearlyCycle = false" class="left-selector">{{ $gettext('Monthly') }}</span>
                  <b-form-checkbox v-model="yearlyCycle" switch size="lg">
                    <span class="right-selector">{{ $gettext("Yearly") }} <span class="yearly-offer">{{ discountText }}</span></span>
                  </b-form-checkbox>
                </div>
              </div>

              <div v-if="modifyPlan && !billingSettings.cancelled" class="plan-box-row">
                <GekkoButton
                  @click="updateSubscription()"
                  :text="$gettext('Update subscription')"
                  size="lg"
                  class="position-relative"
                  :loading="isWaitingForResponse"
                />
              </div>
              
            </div>
            <div class="plan-box-item">
              <div class="plan-box-item__title">
                {{ $gettext('Next payment') }}
                <font-awesome-icon id="tooltip-subscriptions" class="tooltip-icon tooltip-icon--question ml-2" icon="fa-regular fa-circle-question" />
                <b-tooltip :custom-class="'tooltip-padded'" target="tooltip-subscriptions" triggers="hover" container="#report-container">
                  <div class="tooltip-title">
                      {{ $gettext('Subscriptions') }}
                  </div>
                  <div class="tooltip-text">
                      {{ $gettext('Gekko Premium is a subscription for which automatic recurring payments are required. You can cancel your subscription at any time before this date at no expense and your Premium subscription will automatically stop.') }}
                  </div>
                </b-tooltip>
              </div>
              <div v-if="!billingSettings.cancelled">
                <div v-if="isActive">
                  <p>
                    <strong>{{ activePlan.end_date | formatDate }}</strong>
                  </p>

                  <p v-if="currentCycle === 12" v-translate='{ total_discount_next: totalDiscountNext }'>
                    You save <span class="text-brand">€%{total_discount_next}</span> by billing annually.
                  </p>

                  <div style="font-size: 40px; font-family: DINNextLTPro-Medium, sans-serif;">
                    <div v-html="beautifiedNextPayment" />
                    <span style="display:block; font-size: 14px; opacity: 0.6;">{{ $gettext('VAT applies') }}</span>
                  </div>
                  <br>
                  <router-link :to="{ name: 'DowngradeSubscription' }">{{ $gettext('Cancel subscription') }}</router-link>
                </div>
                <div v-if="isGrace">
                  <b-alert show class="gk-alert mb-0" variant="warning">
                    <div class="grace-payment-alert">
                      <p class="mb-0" v-translate='{ grace_ends: $options.filters.formatDate(activePlan.grace_date) }'>
                        Your payment was not successful. You will therefore be downgraded on %{grace_ends} unless you complete the Premium payment before that time.
                      </p>
                      <GekkoButton
                        @click="repayPlan()"
                        icon="fa-regular fa-credit-card"
                        :text="$gettext('Retry payment')" 
                        class="mt-2 position-relative"
                        :loading="isWaitingForResponse"
                      />
                    </div>
                  </b-alert>
                  <!-- GRACE Your next payment will be on <strong>{{ activePlan.end_date | formatDate }}</strong> -->
                </div>
                <div v-if="isRenewalDisabled">
                  <b-alert show class="gk-alert mb-0" variant="warning">
                    <div class="grace-payment-alert">
                      <p class="mb-0">
                        <span translate>Your subscription cannot be renewed automatically. This means you will be automatically downgraded after your subscription runs out. Please contact customer support by emailing support@getgekko.com to check why your subscription cannot be autorenewed.</span>
                      </p>
                    </div>
                  </b-alert>
                </div>
              
              </div>
              <div v-else>
                <span class="text-danger">{{ $gettext('Your subscription was cancelled on') }} {{ billingSettings.cancelled | formatDate }}</span>.
                <span v-if="hasPremiumAccessUntilEndDate">
                  <br>
                  {{ $gettext('Your access to premium features will end on') }} {{ activePlan.end_date | formatDate }}
                </span>
                <br>
                <GekkoButton
                  @click="reactivatePlan()"
                  :text="$gettext('Reactivate subscription')"
                  size="lg"
                  class="mt-2 position-relative"
                  :loading="isWaitingForResponse"
                />
              </div>
            </div>
          </div>
        </section>
      </div>

      <AddPremiumReceiptAsCost />

      <BillingInvoices />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from "vuex";
import settingsPageSetup from "@/mixins/settingsPageSetup"
import AddPremiumReceiptAsCost from "@/components/subscriptions/AddPremiumReceiptAsCost";
import GekkoButton from '@/components/GekkoButton.vue'
import GekkoRangeInput from '@/components/ui/GekkoRangeInput.vue'
import PlanBoxBasic from "@/components/subscriptions/PlanBoxBasic"
import PlanBoxPremium from "@/components/subscriptions/PlanBoxPremium"
import GekkoBillingSwitch from '@/components/ui/GekkoBillingSwitch.vue'
import TeamsizeSelector from "@/components/subscriptions/TeamsizeSelector"

import PlanService from "@/store/services/plan.service"
import BillingSettingService from "@/store/services/billingsetting.service";
import BillingInvoices from "@/components/invoices/BillingInvoices";

import moment from 'moment';

export default {
  name: 'Subscriptions',
  components: {
    AddPremiumReceiptAsCost,
    GekkoButton,
    GekkoRangeInput,
    PlanBoxBasic,
    PlanBoxPremium,
    GekkoBillingSwitch,
    TeamsizeSelector,
    BillingInvoices
  },
  mixins: [ settingsPageSetup ],
  data () {
    return {
      loading: true,

      pageSettings: {
        title: this.$gettext('My subscription'),
        showCompanyPicker: false
      },

      fraud_offset: 100,  // set as fraud by default
      fraud_warning_visibility: false,
      changeBillingCycleVariant: 'manage',
      mobile_app_view: null,

      teamRangeMax: 10,
      yearlyCycle: false,

      billingswitch: 'monthly', // preselected to monthly

      showDetails: false,
      showBillingSwitch: false,
    }
  },
  computed:{
    ...mapGetters({ 
      isPremium: "getIsPremium",
      activeDiscount: 'subscriptionStore/getDiscount',
      activePlan: 'subscriptionStore/getActivePlan',
      storedTeamSize: 'subscriptionStore/getTeamSize',
      currentProducts: 'subscriptionStore/getProducts',
      getProductByType: 'subscriptionStore/getProductByType',
      minTeamSize: 'subscriptionStore/getMinTeamSize',
      billingSettings: 'subscriptionStore/getBillingSettings',
      paymentDetails: 'subscriptionStore/getPaymentDetails',
      nextPayment: 'subscriptionStore/getNextPayment',
      activeTeamMembers: 'team/getActiveTeamMembers',
      pendingInvites: 'team/getInvitationsPerUser',
      preferredLanguage: 'main/getPreferredLanguage',
      getCompanyId: 'main/getCompanyId',
      getMobileAppView: "main/getMobileAppView",
      isFreePremium: 'subscriptionStore/getIsFreePremium',
      isWaitingForResponse: 'main/isWaitingForResponse',
      currentUser: 'main/getCurrentUser',
    }),
    teamSize: {
      get () { return this.storedTeamSize },
      set (value) { this.$store.commit('subscriptionStore/SET_TEAM_SIZE', value) },
    },
    isUserPremium(){
      return this.isPremium && this.activePlan != null;
    },
    teamSizeCost () {
      return this.teamSize * this.getProductByType('team').cost
    },
    currentCycle () {
      return this.billingSettings.renewal_months
    },
    selectedCycleMultiplier () {
      return (this.yearlyCycle) ? 12 : 1
    },
    selectedCycleName () {
      return (this.yearlyCycle) ? this.$gettext('year') : this.$gettext('month')
    },
    selectedCycleNamely () {
      return (this.yearlyCycle) ? this.$gettext('yearly') : this.$gettext('monthly')
    },
    currentCycleNamely () {
      return (this.currentCycle === 12) ? this.$gettext('yearly') : this.$gettext('monthly')
    },
    rawFullPrice () {
      return (this.getProductByType('premium').cost + this.teamSizeCost) * this.selectedCycleMultiplier
    },
    rawCyclePrice () {
      return this.getProductByType('premium').cost * this.selectedCycleMultiplier
    },
    rawTeamPrice () {
      return this.teamSizeCost * this.selectedCycleMultiplier
    },
    totalDiscount () {
      let discount
      if (this.activeDiscount.benefit_type === 'amount') {
        // discount = Math.round(this.activeDiscount.amount)
        discount = this.activeDiscount.amount
      } else {
        let percentage = this.activeDiscount.amount * 0.01 // from % to a percentage
        // discount = Math.round(percentage * this.rawFullPrice)
        discount = percentage * this.rawFullPrice
      }
      return parseFloat(discount).toFixed(2)
    },
    discountText () {
      let discount_text = '';
      let discount_amount = this.activeDiscount.amount
      if (this.activeDiscount.benefit_type === 'amount') {
        discount_text = this.$gettext('%{ amount }€ OFF!')
      } else {
        discount_text = this.$gettext('%{ amount }% OFF!')
        discount_amount = (discount_amount % 1 !== 0) ? discount_amount : Math.round(discount_amount)
      }
      return this.$gettextInterpolate(discount_text, { amount: discount_amount });
    },
    finalPrice () {
      return (this.yearlyCycle) ? this.rawFullPrice - this.totalDiscount : this.rawFullPrice
    },
    planPriceData () {
      return {
        yearlyCycle: this.yearlyCycle,
        teamSizeCost: this.teamSizeCost,
        rawFullPrice: this.rawFullPrice,
        finalPrice: this.finalPrice,
        selectedCycleName: this.selectedCycleName,
        rawCyclePrice: this.rawCyclePrice,
        teamSize: this.teamSize,
        rawTeamPrice: this.rawTeamPrice,
        totalDiscount: this.totalDiscount
      }
    },
    updatedCycle () {
      let current_yearly = (this.currentCycle === 12)
      return (this.yearlyCycle !== current_yearly)
    },
    updatedTeamSize () {
      return (this.teamSize > this.minTeamSize)
    },
    modifyPlan () {
      return this.updatedTeamSize || this.updatedCycle
    },
    updateAmountPay () {
      let amount = 0
      if (this.updatedTeamSize) {
        const new_users = (this.teamSize - this.minTeamSize)

        // Define two date objects
        const end_date = new Date(this.activePlan.end_date);
        const start_date = new Date(this.activePlan.start_date);

        // Convert the difference to days
        const total_plan_days = (end_date.getTime() - start_date.getTime()) / (1000 * 60 * 60 * 24)
        const total_days_left = (end_date.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
        const time_left_fraction = total_days_left / total_plan_days

        amount = new_users * this.getProductByType('team').cost * time_left_fraction
      }
      // TODO should this amount use current plan discount?
      return amount
    },
    isActive () {
      return this.activePlan?.status === 'active'
    },
    isGrace () {
      return this.activePlan?.grace_date !== null
    },
    isRenewalDisabled (){
      return !this.billingSettings.auto_renewal;
    },
    hasPremiumAccessUntilEndDate () {
      return (new Date().getTime() < new Date(this.activePlan.end_date).getTime())
    },
    premiumCost () {
      return this.getProductByType('premium').cost * ((this.yearlyCycle) ? 12 : 1)
    },
    teamCost () {
      return this.getProductByType('team').cost * ((this.yearlyCycle) ? 12 : 1)
    },
    usedTeamSlots () {
      let used_slots = this.activeTeamMembers.length + this.pendingInvites.length
      return (used_slots - 1) // not counting self
    },
    teamSizeCostNext () {
      return this.usedTeamSlots * this.getProductByType('team').cost
    },
    rawFullPriceNext () {
      return (this.getProductByType('premium').cost + this.teamSizeCostNext) * this.selectedCycleMultiplier
    },
    totalDiscountNext () {
      let discount
      if (this.activeDiscount.benefit_type === 'amount') {
        // discount = Math.round(this.activeDiscount.amount)
        discount = this.activeDiscount.amount
      } else {
        let percentage = this.activeDiscount.amount * 0.01 // from % to a percentage
        // discount = Math.round(percentage * this.rawFullPriceNext)
        discount = percentage * this.rawFullPriceNext
      }
      return parseFloat(discount).toFixed(2)
    },
    endBillingDate () {
      let end_date
      let dateformat = (this.preferredLanguage === 'en') ? 'MM/DD/YYYY' : 'DD/MM/YYYY'
      if (this.yearlyCycle) {
        end_date = moment(Date.now()).add(1, 'years').format(dateformat)
      } else {
        end_date = moment(Date.now()).add(1, 'months').format(dateformat)
      }
      return end_date
    },
    beautifiedNextPayment () {
      if (this.nextPayment) {
        const amount = this.nextPayment.toFixed(2)
        const decimal_part = amount.split('.')[1]
        return '€' + Math.floor(amount) + '<span style="font-size: 20px;">.' + decimal_part + '</span>'
      }
      return ''
    }
  },
  created () {
    this.mobile_app_view = this.getMobileAppView

    this.initData()
  },
  methods:{
    ...mapActions({ 
      loadTeamMainData: 'team/loadTeamMainData',
      loadSubscriptionsMainData: 'subscriptionStore/loadSubscriptionsMainData',
      storeUpdateSubscription: 'subscriptionStore/updateSubscription'
    }),
    ...mapMutations({
      setWaitingForResponse: 'main/SET_WAITING_FOR_RESPONSE'
    }),
    initData () {
      this.loading = true
      return this.loadSubscriptionsMainData().then(() => {
        if(this.$route.query.selected === "year"){
          this.yearlyCycle = true;
        }
        if(this.isPremium) {
          this.yearlyCycle = (this.currentCycle === 12)
        }
        
        this.loadTeamMainData().then(() => {
          this.loading = false
        })
      })
    },
    // TODO Move this to subscriptionStore
    createPlan () {
      PlanService.create({ company_id: this.getCompanyId, months: this.selectedCycleMultiplier, team_size: this.teamSize }).then((response) => {
        var a_transaction_id = response.data;
        this.$router.push({ name: 'ConfirmPayment', params: { transactionId: a_transaction_id } });
      })
    },
    upgradeFreePlan() {
      PlanService.upgradeFreePlan(this.activePlan.id, {months: this.selectedCycleMultiplier, team_size: this.teamSize }).then((response) => {
        var a_transaction_id = response.data;
        this.$router.push({ name: 'ConfirmPayment', params: { transactionId: a_transaction_id } });
      })
    },
    updateSubscription () {
      this.setWaitingForResponse(true)
      let alert_text = []
      let show_alert = false
    
      if (this.updatedCycle) {
        alert_text.push(this.$gettext('Updated billing Cycle'))
        show_alert = true
      }

      if (this.updatedTeamSize) {
        alert_text.push(this.$gettext('Updated team size'))
        show_alert = false
      }

      this.storeUpdateSubscription({
          router: this.$router,
          payload: {
              yearlyCycle: this.yearlyCycle,
              updatedCycle: this.updatedCycle,
              teamMembers: this.teamSize - this.minTeamSize,
              updatedTeamSize: this.updatedTeamSize
          }
      })
      .then(() => {
        this.initData().then(() => {
          if (show_alert) {
            this.$store.commit('setAlert', {
              title: this.$gettext('Subscription Updated'),
              text: alert_text.join('. ') + '.',
              variant: 'success',
              duration: 4
            })
          }
        })
      })
      .catch((err) => {
        console.error(err)
      }).finally(() => {
        this.setWaitingForResponse(false)
      })
    },
    decreaseTeamSize () {
      if (this.teamSize > this.minTeamSize) this.teamSize--
    },
    increaseTeamSize () {
      this.teamSize++
    },
    repayPlan(){
      this.setWaitingForResponse(true)
      PlanService.repayPlan().then((response) => {
        this.$router.push({ name: 'ConfirmPayment', params: { transactionId: response.data } });
      }).catch((response)=>{
        if(response.status === 404){
          this.$store.commit('setAlert', {title: this.$gettext("Error"), text:response.data.detail, variant:"danger",duration:4});
        }
      }).finally(() => {
        this.setWaitingForResponse(false)
      })
    },
    reactivatePlan () {
      this.setWaitingForResponse(true)
      BillingSettingService.uncancelPremium().then(() => {
        this.initData().then(() => {
          this.$store.commit('setAlert', {
            title: this.$gettext('Subscription reactivated'),
            text: this.$gettext('Your account has been reactivated.'),
            duration: 4
          })
        })
      }).finally(() => {
        this.setWaitingForResponse(false)
      })
    }
  }
};
</script>

<style lang="scss">
  .plan-cycle-switch-wrap {
    display: flex;
    justify-content: center;
    align-items: center;
    .custom-control-input:focus ~ .custom-control-label::before { box-shadow: none !important; }
    .custom-control-input:not(:disabled):active ~ .custom-control-label::before { background-color: $colorBrand; border-color: $colorBrand; }
    .left-selector { padding-right: 0.75rem; cursor: default; margin-bottom: -2px; }
    .left-selector, .right-selector { 
      cursor: pointer;
      font-size: 1rem;
      white-space: nowrap;
      user-select: none;
      .yearly-offer {
        color: $colorBrand;
        font-family: DINNextLTPro-Medium, sans-serif;
      }
    }
  }
</style>
    
<style lang="scss">
.subscriptions-wrap {
  position: relative;
  z-index: 2;
  section { padding: 1.5rem 0; }
  .premium-subscription-wrapper {
    padding-top: 0;
    width: 100%;
    .plan-box {
      display: block;
      max-width: 100%;
      text-align: left;
      background: white;
      padding: 1rem;
      overflow: hidden;
      font-size: 1rem;
      .plan-box-item {
        padding: 1rem 0;
        &:last-child { border-top: 1px solid $colorBorderDash; }
        &__title {
          font-size: 14px;
          text-transform: uppercase;
          margin-bottom: 0.5rem;
          color: #8c8c8c;
        }
      }
      .plan-box-row {
        display: flex;
        padding: 1rem 0;
        gap: 2rem;
        flex-direction: column;
        &--actions {
          padding: 1.5rem 0;
          border-top: 1px solid $colorBorderDash;
        }
        &.update-subscription {
          background: #fff;
          align-items: center;
          p { font-size: 14px; }
          border: 1px solid $colorBrand;
          border-radius: $radiusLg;
          .plan-box-section {
            &:first-child { flex: 1; }
            &:last-child { flex: 0; }
          }
          .plan-box-section {
            border: none !important;
          }
        }

        @media (min-width: '992px') {
          padding: 1.5rem 0;
          flex-direction: row;
          &--actions {
            padding: 2rem 0;
            border-top: 1px solid $colorBorderDash;
          }
        }

      }
      .plan-box-section {
        padding: 0 1rem;
        padding: 0;
        h3 { font-size: 22px; }
        flex: 1;
        p.subtitle { opacity: 0.6; }
        p { 
          font-size: 1rem;
          margin-bottom: 0.5rem;
          &:last-child { margin-bottom: 0; }
        }
        .plan-cycle-switch-wrap { justify-content: flex-start; }
        .gk-table {
          width: 100%;
          thead {
            background: transparent;
            tr td { padding: 0.5rem 0.75rem; }
          }
          tbody tr { cursor: initial; }
        }
        .teamsize-description-text {
          opacity: 1 !important;
        }
        &.payment-details {
          .payment-details-wrap {
            display: flex;
            flex-wrap: wrap;
            gap: 2rem;
          }
          .payment-details-item {
            display: flex;
            flex-direction: column;
            .payment-details-label {
              text-transform: uppercase;
              font-size: 14px;
              opacity: 0.6;
              display: block;
            }
            .payment-details-data {
              font-size: 1rem;
            }
          }
        }
        @media (min-width: '768px') {
          padding: 0 1.5rem;
        }
      }
      
    }
  }

  .update-teamsize-selector {
    border-radius: 8px;
    outline: 1px solid #dbdbde;
    font-size: 20px;
    padding: 0.5rem 0.25rem;
    display: inline-block;
    white-space: nowrap;
    > span { padding: 0 0.5rem; }
    button {
      background: transparent !important;
      color: $colorBrand !important;
      border: none !important;
      padding: 2px 0.5rem;
      &.disabled { color: $colorUnassigned !important; }
    }
  }

  .plan-box {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    flex: 1;
    width: 100%;
    padding: 1rem;
    border-radius: $radiusLg;

    box-shadow: $settingShadow;

    h2 { margin: 0; }

    .price-wrap {
      margin-bottom: 1.5rem;

      flex: 1;
      align-items: center;
      display: flex;
      flex-direction: column;
      justify-content: center;
      border-radius: 12px; 
      .main-price {
        font-size: 50px;
        font-family: DINNextLTPro-Medium, sans-serif;
        position: relative;
        line-height: 1;
        color: #0d0c22;
        .price-fraction { font-size: 20px; }
        .pre-sale {
          font-size: 18px;
          text-decoration: line-through;
          opacity: 0.6;
        }
        span {
          font-size: 20px;
          font-family: DINNextLTPro-Regular, sans-serif;
          opacity: 0.8;
        }
      }
      .price-breakdown {
        font-size: 14px;
        color: #3d3d4e;
        display: flex;
        align-items: center;
        .breakdown-discount {
          color: $colorBrand;
          margin: 0.5rem 0;
          display: flex;
          align-items: baseline;
          line-height: 1rem;
          position: relative;
          .savings {
            font-size: 14px;
            background: #24ac8a1a;
            padding: 8px 12px;
            border-radius: 50em;
          }
          svg.icon-arrow {
            width: 24px;
            height: 24px;
            position: absolute;
            right: -24px;
            top: -4px;
          }
          .svg-inline--fa { margin-right: 0.5rem; color: $colorBrand; }
          .saving { color: $colorBrand; }
          &.not-applied {
            color: $colorBrand;
            background: transparent;
          }
        }
      }
    }

    .plan-box-upgrade-teamsize {
      .upgrade-teamsize-content-wrap {
        display: flex; 
        flex: 1; 
        gap: 1rem; 
        align-items: center;
        &.non-premium {
          justify-content: space-between;
          .strong {
            font-size: 18px; 
            font-family: DINNextLTPro-Medium, sans-serif;
          }
        }
        .teamsize-selector-wrap {
          text-align: center; 
          display: flex; 
          align-items: center; 
          .teamsize-selector__icon {
            margin-right: 0.75rem; 
            opacity: 0.3;
            > svg { width: 30px; height: 30px; }
          }
        }
        @media (min-width: '768px') {
          .teamsize-description-text {
            font-size: 1rem; 
          }
          .teamsize-selector-wrap {
            // padding-top: 0.5rem;
            .teamsize-selector__icon {
              > svg { width: 30px; height: 30px; }
            }
          }
        }
      }
    }

    &.plan-premium { 
      background-color: #fff;
      align-items: flex-start;
      text-align: left;
      width: fit-content;
      .price-wrap { text-align: center; }
      .btn-lg { font-size: 22px; }
    }
  }
}
.subscriptions-wrap {
  @media (min-width: '768px') {
    .plan-box {
      padding: 1.5rem;
    }
  }
}

.settings-separator {
  margin: 3rem 0;
  border-top: 1px solid $colorBorderDash;
  opacity: 0.5;
}
.add-users-box-wrap {
  padding-bottom: 2rem;
  .add-users-box {
    padding: 1rem;
    border-radius: $radiusLg;
    background: white;
    display: inline-block;
    color: $colorBrand;
    font-size: 16px;
    box-shadow: $settingShadow;
    a {
      color: $colorBlue;
      text-decoration: none;
    }
  }
}

</style>
